package org.catharsis.spendtel.feature.presentation.home

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.ExpenseReportStatus
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.presentation.expensereport.ExpenseReportStatusChipCompact
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelGradients
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors

fun latestExpenseReportId(reports: List<ExpenseReport>): String? {
    return reports.maxByOrNull { it.createdAtMillis }?.id
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun ExpenseReportListItem(
    report: ExpenseReport,
    onClick: () -> Unit,
    onLongClick: () -> Unit,
    isDeleting: Boolean = false,
    isLatest: Boolean = false,
    cardIndex: Int = 0,
    modifier: Modifier = Modifier,
) {
    val gradient = SpendTelGradients.wrappedForIndex(cardIndex)
    val periodLabel = report.statementPeriodLabel

    val heroText = when {
        report.isProcessing -> "Unwrapping…"
        report.monthlyStory?.headline != null -> report.monthlyStory.headline
        report.financialPersonality?.label != null -> report.financialPersonality.label
        report.summary != null -> formatCurrency(
            report.summary.totalSpending,
            report.summary.currency,
        )
        else -> "Your story"
    }

    val subtitle = when {
        report.isProcessing -> null
        report.financialPersonality?.label != null &&
            report.monthlyStory?.headline != null -> report.financialPersonality.label
        report.summary != null && report.monthlyStory?.headline != null ->
            formatCurrency(report.summary.totalSpending, report.summary.currency)
        else -> null
    }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 160.dp)
            .clip(RoundedCornerShape(24.dp))
            .combinedClickable(
                enabled = !isDeleting,
                onClick = onClick,
                onLongClick = onLongClick,
            )
            .background(gradient),
    ) {
        WrappedCardDecoration()

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 20.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = periodLabel.uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.White.copy(alpha = 0.72f),
                    letterSpacing = 1.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )

                if (isDeleting) {
                    CircularProgressIndicator(
                        modifier = Modifier
                            .padding(start = 12.dp)
                            .size(22.dp),
                        color = Color.White,
                        strokeWidth = 2.dp,
                    )
                } else {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.padding(start = 12.dp),
                    ) {
                        if (isLatest) {
                            HomeReportLatestLabel(lightOnDark = true)
                        }
                        ExpenseReportStatusChipCompact(status = report.status)
                    }
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = heroText,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = Color.White,
                    lineHeight = 28.sp,
                )
                subtitle?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.bodyMedium,
                        color = Color.White.copy(alpha = 0.88f),
                        fontWeight = FontWeight.Medium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }

            if (!isDeleting && ExpenseReportStatus.isComplete(report.status)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End,
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                        contentDescription = null,
                        tint = Color.White.copy(alpha = 0.85f),
                        modifier = Modifier.size(28.dp),
                    )
                }
            }
        }
    }
}

@Composable
fun HomeReportLatestLabel(
    modifier: Modifier = Modifier,
    lightOnDark: Boolean = false,
) {
    Surface(
        modifier = modifier,
        color = if (lightOnDark) {
            Color.White.copy(alpha = 0.22f)
        } else {
            SpendTelReportColors.violet.copy(alpha = 0.12f)
        },
        contentColor = if (lightOnDark) Color.White else SpendTelReportColors.violet,
        shape = RoundedCornerShape(999.dp),
    ) {
        Text(
            text = "LATEST",
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
            maxLines = 1,
            softWrap = false,
        )
    }
}

@Composable
private fun BoxScope.WrappedCardDecoration() {
    Box(
        modifier = Modifier
            .size(90.dp)
            .align(Alignment.TopEnd)
            .padding(top = 8.dp, end = 8.dp)
            .clip(CircleShape)
            .background(Color.White.copy(alpha = 0.12f)),
    )
    Box(
        modifier = Modifier
            .align(Alignment.BottomEnd)
            .offset(x = 12.dp, y = 12.dp)
            .size(48.dp)
            .clip(CircleShape)
            .background(Color.White.copy(alpha = 0.08f)),
    )
}
